/**
 * TableOfContentsBlock Component
 *
 * Custom BlockNote React component that generates an interactive table of contents
 * from document headings. Provides clickable jump links and real-time synchronization.
 *
 * Features:
 * - Automatic extraction of heading blocks
 * - Real-time updates when headings change
 * - Configurable heading level inclusion via toolbar
 * - Per-instance visibility controls with eye icons
 * - Jump link navigation with scroll offset
 * - Empty state placeholder
 * - Nested hierarchy with 20px indentation
 * - Hover-triggered toolbar for settings
 * - Performance optimized for 100+ headings
 * - Full accessibility support (WCAG AA)
 * - Keyboard navigation and screen reader compatible
 * - Responsive design for mobile and desktop
 *
 * Props (from block.props):
 * - includedLevels: Array of heading levels to include (default: [2, 3])
 * - hiddenHeadings: Array of heading IDs to hide (default: [])
 *
 * Usage:
 *   This component is automatically rendered when a "tableOfContents" block
 *   is present in the BlockNote document.
 *
 * Related Files:
 * - app/javascript/controllers/blocks_text_editor_controller.js
 * - app/javascript/components/blocknote/utils/headingExtractor.js
 * - app/javascript/components/blocknote/TOCToolbar.jsx
 * - app/javascript/components/blocknote/TOCItem.jsx
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { extractHeadings } from "./utils/headingExtractor.js";
import TOCToolbar from "./TOCToolbar.jsx";
import TOCItem from "./TOCItem.jsx";

/**
 * Empty State Component
 *
 * Displays a placeholder message when no headings match the filter criteria.
 * Includes ARIA attributes for accessibility.
 */
function EmptyState() {
  return (
    <div
      className="toc-empty-state text-neutral-500 text-sm italic py-4 px-2"
      role="status"
      aria-live="polite"
    >
      Table of Contents: nothing to show currently, please add headings to your
      document or adjust the settings above.
    </div>
  );
}

/**
 * Main TableOfContentsBlock Component
 *
 * Renders the full table of contents with real-time updates, navigation,
 * and interactive controls for customization.
 *
 * @param {Object} props
 * @param {Object} props.block - The BlockNote block data
 * @param {Object} props.editor - The BlockNote editor instance
 */
export default function TableOfContentsBlock({ block, editor }) {
  const [headings, setHeadings] = useState([]);
  const [updateCounter, setUpdateCounter] = useState(0);
  const [isEditMode, setIsEditMode] = useState(true);
  const tocRef = useRef(null);
  const updateTimerRef = useRef(null);

  // Get current props with defaults
  const includedLevels = block.props?.includedLevels || [2, 3];
  const hiddenHeadings = block.props?.hiddenHeadings || [];

  // Compute list to render and count: in view mode, omit hidden headings entirely.
  const renderHeadings = React.useMemo(() => {
    if (isEditMode) return headings;
    const hiddenSet = new Set(hiddenHeadings);
    return headings.filter((h) => !hiddenSet.has(h.id));
  }, [headings, hiddenHeadings, isEditMode]);

  /**
   * Detect if we're in edit mode or view mode
   * Check if the editor is editable (viewer sets editable={false})
   */
  useEffect(() => {
    const editMode = editor && editor.isEditable;
    setIsEditMode(editMode);
  }, [editor]);

  /**
   * Extracts and updates the headings list
   *
   * Reads the current document structure and filters headings based on
   * the block's configured includedLevels and hiddenHeadings props.
   * Optimized for performance with large documents.
   */
  const updateHeadings = useCallback(() => {
    try {
      const document = editor.document;
      const extractedHeadings = extractHeadings(document, {
        includedLevels,
        hiddenHeadings: [], // Don't filter hidden headings in extraction
      });

      setHeadings(extractedHeadings);
    } catch (error) {
      // Swallow extraction errors; render empty TOC
      setHeadings([]);
    }
  }, [editor, includedLevels]);

  /**
   * Debounced update handler
   *
   * Prevents flickering by debouncing rapid document changes.
   * Uses a counter-based approach to batch updates for better performance.
   */
  useEffect(() => {
    // Clear any existing timer
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    // Set new debounced update
    updateTimerRef.current = setTimeout(() => {
      updateHeadings();
    }, 100); // 100ms debounce

    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, [updateHeadings, updateCounter]);

  /**
   * Listen for document changes
   *
   * Subscribes to editor changes and triggers heading updates.
   * Real-time synchronization when headings are added, removed, or modified.
   * Optimized to minimize re-renders.
   */
  useEffect(() => {
    // Initial extraction
    updateHeadings();

    // Subscribe to changes (only in edit mode)
    if (isEditMode) {
      const unsubscribe = editor.onChange(() => {
        // Trigger debounced update by incrementing counter
        setUpdateCounter((prev) => prev + 1);
      });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [editor, updateHeadings, isEditMode]);

  /**
   * Updates the block's includedLevels prop
   *
   * Uses BlockNote's updateBlock API to persist the change to the document.
   * Only available in edit mode.
   *
   * @param {Array<number>} newLevels - Updated array of included heading levels
   */
  const handleLevelsChange = useCallback(
    (newLevels) => {
      if (!isEditMode) return;

      try {
        editor.updateBlock(block, {
          props: {
            ...block.props,
            includedLevels: newLevels,
          },
        });
      } catch (error) {
        console.error("Error updating included levels:", error);
      }
    },
    [editor, block, isEditMode],
  );

  /**
   * Toggles visibility of a specific heading
   *
   * Adds or removes the heading ID from the hiddenHeadings array
   * and updates the block props. Only available in edit mode.
   *
   * @param {string} headingId - ID of the heading to toggle
   */
  const handleToggleVisibility = useCallback(
    (headingId) => {
      if (!isEditMode) return;

      try {
        const currentHidden = block.props?.hiddenHeadings || [];
        let newHidden;

        if (currentHidden.includes(headingId)) {
          // Remove from hidden list
          newHidden = currentHidden.filter((id) => id !== headingId);
        } else {
          // Add to hidden list
          newHidden = [...currentHidden, headingId];
        }

        editor.updateBlock(block, {
          props: {
            ...block.props,
            hiddenHeadings: newHidden,
          },
        });
      } catch (error) {
        console.error("Error toggling heading visibility:", error);
      }
    },
    [editor, block, isEditMode],
  );

  /**
   * Handles jump link clicks
   *
   * Scrolls to the target heading with a 30px offset from the top.
   * Uses instant scrolling for immediate response.
   * Manages focus for accessibility after navigation.
   *
   * @param {Event} e - Click event
   * @param {String} headingId - ID of the target heading
   */
  const handleJumpClick = (e, heading) => {
    e.preventDefault();

    const headingId = heading?.id;
    const blockId = heading?.blockId;

    try {
      // Try the most direct: element with matching id
      let element = document.getElementById(headingId);

      // Fall back to BlockNote data attributes if the id is not present
      if (!element) {
        element =
          document.querySelector(
            `.bn-block-outer[data-id="${CSS.escape(headingId)}"]`,
          ) ||
          document.querySelector(
            `.bn-block[data-node-type="blockContainer"][data-id="${CSS.escape(headingId)}"]`,
          ) ||
          document.querySelector(
            `.bn-block-content[data-content-type="heading"][data-id="${CSS.escape(headingId)}"]`,
          );
      }

      // Try using the BlockNote block id if provided
      if (!element && blockId) {
        element =
          document.querySelector(
            `.bn-block-outer[data-id="${CSS.escape(blockId)}"]`,
          ) ||
          document.querySelector(
            `.bn-block[data-node-type="blockContainer"][data-id="${CSS.escape(blockId)}"]`,
          );
      }

      // Also try matching heading tag with id as last resort (SSR path)
      if (!element) {
        element = document.querySelector(
          `h1#${CSS.escape(headingId)},h2#${CSS.escape(headingId)},h3#${CSS.escape(headingId)},h4#${CSS.escape(headingId)},h5#${CSS.escape(headingId)},h6#${CSS.escape(headingId)}`,
        );
      }

      if (element) {
        // Calculate scroll position with 30px offset
        const offset = 30;
        const elementTop = element.getBoundingClientRect().top;
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = elementTop + scrollTop - offset;

        // Instant scroll to target
        window.scrollTo({
          top: targetPosition,
          behavior: "instant",
        });

        // Focus management for accessibility
        // Make the heading focusable if it isn't already
        if (!element.hasAttribute("tabindex")) {
          element.setAttribute("tabindex", "-1");
        }

        // Focus the heading for screen reader announcement
        element.focus({ preventScroll: true });

        // Announce to screen readers
        const announcement = document.createElement("div");
        announcement.setAttribute("role", "status");
        announcement.setAttribute("aria-live", "polite");
        announcement.className = "sr-only";
        announcement.textContent = `Navigated to ${element.textContent}`;
        document.body.appendChild(announcement);
        setTimeout(() => document.body.removeChild(announcement), 1000);
      }
    } catch (error) {}
  };

  /**
   * Handle keyboard navigation within TOC
   *
   * Arrow keys to navigate between items
   * Enter/Space to activate jump links
   */
  const handleKeyDown = (e) => {
    if (!tocRef.current) return;

    const items = tocRef.current.querySelectorAll(".toc-link");
    const currentIndex = Array.from(items).findIndex(
      (item) => item === document.activeElement,
    );

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < items.length - 1) {
          items[currentIndex + 1].focus();
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          items[currentIndex - 1].focus();
        }
        break;
      case "Home":
        e.preventDefault();
        if (items.length > 0) {
          items[0].focus();
        }
        break;
      case "End":
        e.preventDefault();
        if (items.length > 0) {
          items[items.length - 1].focus();
        }
        break;
    }
  };

  return (
    <nav
      ref={tocRef}
      className="toc-block border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
      aria-label="Table of Contents"
      onKeyDown={handleKeyDown}
    >
      <div className="toc-header mb-3 flex items-center justify-between">
        <h3
          className="text-sm font-semibold text-neutral-900 uppercase tracking-wide"
          id={`toc-heading-${block.id}`}
        >
          Table of Contents
        </h3>
        {renderHeadings.length > 0 && (
          <span
            className="text-xs text-neutral-500"
            aria-label={`${renderHeadings.length} headings`}
          >
            {renderHeadings.length}{" "}
            {renderHeadings.length === 1 ? "item" : "items"}
          </span>
        )}
      </div>

      {/* Toolbar - always shown in edit mode */}
      {isEditMode && (
        <TOCToolbar
          includedLevels={includedLevels}
          onLevelsChange={handleLevelsChange}
        />
      )}

      <div
        className="toc-content"
        role="navigation"
        aria-labelledby={`toc-heading-${block.id}`}
      >
        {renderHeadings.length > 0 ? (
          <ul className="toc-list space-y-1 list-none m-0 p-0" role="list">
            {renderHeadings.map((heading, index) => (
              <TOCItem
                key={heading.id || index}
                heading={heading}
                isHidden={hiddenHeadings.includes(heading.id)}
                onJumpClick={handleJumpClick}
                onToggleVisibility={isEditMode ? handleToggleVisibility : null}
                isEditMode={isEditMode}
              />
            ))}
          </ul>
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Performance indicator for large documents (development only) */}
      {process.env.NODE_ENV === "development" && headings.length > 50 && (
        <div className="text-xs text-neutral-400 mt-2 italic">
          {headings.length} headings loaded
        </div>
      )}
    </nav>
  );
}
